/** Conversión sRGB <-> OKLCH (Ottosson, 2020). Solo lo que necesita `clientAccent`. */

export interface Oklch {
  l: number;
  c: number;
  h: number;
}

const GAMUT_EPSILON = 1e-4;
const CHROMA_STEP = 0.002;

function toLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function fromLinear(channel: number): number {
  return channel <= 0.0031308 ? 12.92 * channel : 1.055 * channel ** (1 / 2.4) - 0.055;
}

function hexToLinearRgb(hex: string): [number, number, number] {
  const channel = (start: number) => toLinear(parseInt(hex.slice(start, start + 2), 16) / 255);
  return [channel(1), channel(3), channel(5)];
}

function linearRgbToHex(rgb: readonly number[]): string {
  const byte = (value: number) =>
    Math.round(Math.max(0, Math.min(1, fromLinear(Math.max(0, Math.min(1, value))))) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${rgb.map(byte).join("").toUpperCase()}`;
}

export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToLinearRgb(hex);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const hue = (Math.atan2(bb, a) * 180) / Math.PI;
  return { l: lightness, c: Math.hypot(a, bb), h: (hue + 360) % 360 };
}

function oklchToLinearRgb({ l, c, h }: Oklch): [number, number, number] {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const lp = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mp = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const sp = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * lp - 3.3077115913 * mp + 0.2309699292 * sp,
    -1.2684380046 * lp + 2.6097574011 * mp - 0.3413193965 * sp,
    -0.0041960863 * lp - 0.7034186147 * mp + 1.707614701 * sp,
  ];
}

const isInGamut = (rgb: readonly number[]) => rgb.every((v) => v >= -GAMUT_EPSILON && v <= 1 + GAMUT_EPSILON);

/** Hex sRGB de un OKLCH; si sale de gama baja la croma en pasos de 0,002 hasta que cabe. */
export function oklchToHexInGamut(l: number, c: number, h: number): string {
  for (let chroma = c; chroma > 0; chroma -= CHROMA_STEP) {
    const rgb = oklchToLinearRgb({ l, c: chroma, h });
    if (isInGamut(rgb)) return linearRgbToHex(rgb);
  }
  return linearRgbToHex(oklchToLinearRgb({ l, c: 0, h }));
}
